import React from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	IconButton,
	ThemeProvider,
	Typography,
} from '@mui/material';
import PrimaryButton from '../button/PrimaryButton';
import theme from 'theme/muiTheme';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { CocAnalysisVO, CocForm, CocSample } from './LabRequests';
interface LabRequestDetailsProps {
	cocForm: CocForm;
	isChampionX: boolean;
	isFoodLab: boolean;
	isOpen: boolean;
    onClose: () => void;
	maxWidthOverride?: number;
	isReview?: boolean;
	reviewFunction?: () => void;
}

interface AddressInfo {
	streetAddress1: string;
	streetAddress2: string;
	city: string;
	officePhone: string;
	countryName: string;
	postalCode: string;
	id: number;
	provinceName: string;
}

const LabRequestDetails: React.FC<LabRequestDetailsProps> = ({
	cocForm,
	isChampionX,
	isFoodLab,
	isOpen,
	onClose,
	maxWidthOverride,
	isReview = false,
	reviewFunction = () => {}
}) => {

	let dateFormatOptions = new Intl.DateTimeFormat("en-CA", {
		month: "long", 
		day: "2-digit", 
		year: "numeric",
		hour: "numeric",
		minute: "numeric",
	});

	let smallerDateFormatOption = new Intl.DateTimeFormat("en-CA", {
		month: "2-digit", 
		day: "2-digit", 
		year: "numeric",
		timeZone: 'UTC',
	})

	const hasSharedAnalyses = React.useMemo(() => {
		if (cocForm.sameAnalysesForAll || cocForm.cocSamples.length === 0) return cocForm.sameAnalysesForAll;

		const firstAnalysisIds = (cocForm.cocSamples[0].cocAnalysisVOs || []).map(analysis => analysis.id);
		return cocForm.cocSamples.every(sample =>
			(sample.cocAnalysisVOs || []).map(analysis => analysis.id).join(',') === firstAnalysisIds.join(',')
		);
	}, [cocForm.cocSamples, cocForm.sameAnalysesForAll]);

	function getAnalysisLabels(sample: any): string[] {
		console.log(sample)
		const analysisNames = new Map((cocForm.cocAnalysisVOs || []).map(analysis => [analysis.id, analysis.name]));
		const labels = (sample.cocAnalysisVOs || [])
			.map((analysis: CocAnalysisVO): string | undefined => analysis.name || analysisNames.get(analysis.id))
			.filter((label: string | undefined): label is string => !!label && label.trim().toLowerCase() !== 'other');
		const customAnalysis = sample.customAnalysis ?? sample.otherAnalysis ?? sample.customAnalysis;
		if (customAnalysis) {
			labels.push(`Other: ${customAnalysis}`);
		}
		return labels;
	}

	function printLabRequest () {
		window.print();
	}

	function createAddressString() {
		let addressString = cocForm.addressInfo.streetAddress1 + ", ";
		if (cocForm.addressInfo.streetAddress2 !== undefined && cocForm.addressInfo.streetAddress2 !== null && cocForm.addressInfo.streetAddress2 !== "") {
			addressString += cocForm.addressInfo.streetAddress2 + ", ";
		}
		const regionString = cocForm.addressInfo.city + ", " + cocForm.addressInfo.provinceName + ", " + cocForm.addressInfo.countryName 
			+ ", " + cocForm.addressInfo.postalCode;
		return (<>{addressString}<br />{regionString}</>);
	}
	return (
		
		<ThemeProvider theme={theme}>
			<Dialog open={isOpen} onClose={onClose} maxWidth={maxWidthOverride ? false : 'md'} fullWidth
				sx={maxWidthOverride ? { '& .MuiDialog-paper': { maxWidth: maxWidthOverride + 'px' } } : {}}>

				<div className='no-print'>
					<IconButton
						onClick={onClose}
						sx={{
							position: 'absolute',
							top: 24,
							right: 24,
							p: 0,
							zIndex: 1000,
						}}
						aria-label="close"
					>
						<img
							src="/assets/icons/Close.svg"
							alt="Close"
							style={{
								width: '20px',
								height: '20px',
							}}
						/>
					</IconButton>
				</div>

				<DialogContent>

					{isReview === true && 
						<Typography variant="h5" gutterBottom sx={{ textAlign: "left", padding: "8px" }}>
							Review Your Submission
						</Typography>
					}
					{isReview === false &&
						<Typography variant="h5" gutterBottom sx={{ textAlign: "left", padding: "8px" }}>
							Chain of Custody #: <strong>{cocForm.cocId}</strong>
						</Typography>
					}

					{isFoodLab ? (
						<>
						<style>{`
							.analysis-item {
								display: block;
							}
							@media print {
								@page { size: landscape; }
								.MuiDialog-paper {
									max-width: 100% !important;
									width: 100% !important;
									margin: 0 !important;
									box-shadow: none !important;
									overflow-x: hidden !important;
								}
								.MuiDialogContent-root {
									overflow-x: hidden !important;
								}
								body {
									overflow-x: hidden !important;
								}
								#userflow-frame, [id^="userflow"], [class^="userflow"] {
									display: none !important;
								}
								title {
									text-align: center !important;
								}
								.analysis-item {
									display: inline;
								}
								.analysis-item:not(:last-child)::after {
									content: ' · ';
								}
								.col-sample-num {
									width: 8% !important;
								}
								.col-client-id {
									width: 23% !important;
								}
								.col-collection-date {
									width: 12% !important;
								}
								.col-comments {
									width: 27% !important;
								}
								.col-analyses {
									width: 30% !important;
								}
								.customer-info-secondary {
									table-layout: fixed;
								}
								.customer-info-secondary td:first-child {
									width: 125px !important;
								}
							}
						`}</style>

						<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', padding: '6px', fontSize: '0.8rem' }}>
							<Box sx={{ width: 'fit-content', maxWidth: '65%' }}>
								<table style={{ width: '100%', fontSize: '0.8rem' }}>
									<tbody>
										<tr>
											<td style={{ paddingRight: 8 }}><strong>Submission date:</strong></td>
											<td>{cocForm.submissionDate ? dateFormatOptions.format(new Date(cocForm.submissionDate)) : ''}</td>
										</tr>
										<tr>
											<td style={{ paddingRight: 8, verticalAlign: 'top' }}><strong>Sent to:</strong></td>
											<td>LuminUltra Technologies Inc. Attn: Food Lab<br/>819 Royal Road, Fredericton, NB E3G 6M1</td>
										</tr>
										<tr>
											<td style={{ paddingRight: 8 }}><strong>Contact name:</strong></td>
											<td>{cocForm.contactName}</td>
										</tr>
										{cocForm.company && (
											<tr>
												<td style={{ paddingRight: 8 }}><strong>Company:</strong></td>
												<td>{cocForm.company}</td>
											</tr>
										)}
										<tr>
											<td style={{ paddingRight: 8, verticalAlign: 'top' }}><strong>Address:</strong></td>
											<td>{createAddressString()}</td>
										</tr>
										<tr>
											<td style={{ paddingRight: 8 }}><strong>Contact phone:</strong></td>
											<td>{cocForm.addressInfo.officePhone}</td>
										</tr>
										<tr>
											<td style={{ paddingRight: 8 }}><strong>Email:</strong></td>
											<td>{cocForm.email}</td>
										</tr>
									</tbody>
								</table>
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<table className="customer-info-secondary" style={{ width: '100%', fontSize: '0.8rem' }}>
									<tbody>
										<tr>
											<td style={{ paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
												<strong>Results{cocForm.collaboratorEmails.length > 0 ? <br /> : ' '}shared with:</strong>
											</td>
											<td>
												{cocForm.collaboratorEmails.length > 0 ? (
													<ul style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
														{cocForm.collaboratorEmails.map((email: string) => (
															<li key={email} style={{ display: 'flex' }}>
																<span style={{ flexShrink: 0, paddingRight: '0.5em', fontSize: '1.6em', lineHeight: 1 }}>•</span>
																<span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{email}</span>
															</li>
														))}
													</ul>
												) : (
													<span>None</span>
												)}
											</td>
										</tr>
										{cocForm.purchaseOrderNumber && (
											<tr>
												<td style={{ paddingRight: 8 }}><strong>Purchase order #:</strong></td>
												<td>{cocForm.purchaseOrderNumber}</td>
											</tr>
										)}
										<tr>
											<td style={{ paddingRight: 8, verticalAlign: 'top' }}><strong>Additional comments:</strong></td>
											<td>{cocForm.specialRequests || 'None'}</td>
										</tr>
									</tbody>
								</table>
							</Box>
						</Box>

						<Typography style={{fontSize:14, fontFamily:"inter", marginTop:8, fontWeight:600, marginBottom:3, paddingLeft:6}} gutterBottom>
							Samples:
						</Typography>
						<Box sx={{ padding: '4px', paddingBottom: '0px', paddingTop: '0px'}}>
							<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', tableLayout: 'fixed' }}>
								<colgroup>
									<col className="col-sample-num" style={{ width: '8%'}} />
									<col className="col-client-id" style={{ width: '30%' }} />
									<col className="col-collection-date" style={{ width: '13%' }} />
									<col className="col-comments" style={{ width: '21%' }} />
									{!hasSharedAnalyses && <col className="col-analyses" style={{ width: '28%' }} />}
								</colgroup>
								<thead>
									<tr style={{ borderBottom: '1px solid #e0e0e0' }}>
										<th style={{ fontWeight: 600, padding: '2px 3px' }}>Sample #</th>
										<th style={{ fontWeight: 600, padding: '2px 9px', textAlign: 'left' }}>Client Sample ID</th>
										<th style={{ fontWeight: 600, padding: '2px 3px', textAlign: 'left' }}>Date of Sampling</th>
										<th style={{ fontWeight: 600, padding: '2px 9px', textAlign: 'left' }}>Comments</th>
										{!hasSharedAnalyses && (
											<th style={{ fontWeight: 600, padding: '2px 3px', textAlign: 'left' }}>Analyses</th>
										)}
									</tr>
								</thead>
									<tbody>
										{cocForm.cocSamples.map((sample: CocSample) => (
											<tr key={sample.inventoryId} style={{ borderBottom: '1px solid #e0e0e0' }}>
												<td style={{ padding: '2px 3px', textAlign: 'center' }}>
													{sample.inventoryId ? parseInt(sample.inventoryId.slice(-2), 10) : cocForm.cocSamples.indexOf(sample) + 1}
												</td>
												<td style={{ padding: '2px 9px', textAlign: 'left' }}>{sample.clientSampleId}</td>
												<td style={{ padding: '2px 3px', textAlign: 'left' }}>{smallerDateFormatOption.format(new Date(sample.collectionDate))}</td>
												<td style={{ padding: '2px 9px', textAlign: 'left' }}>{sample.comments || ''}</td>
													{!hasSharedAnalyses && (
													<td style={{ padding: '2px 3px', textAlign: 'left' }}>
														{getAnalysisLabels(sample).map((label: string, i: number) => (
															<span key={i} className="analysis-item">{label}</span>
														))}
													</td>
												)}
											</tr>
										))}
									</tbody>
							</table>
						</Box>

						{hasSharedAnalyses && cocForm.cocSamples[0] && (
							<>
							<Typography style={{fontSize:14, fontFamily:"inter", marginTop:8, fontWeight:600, marginBottom:4, paddingLeft:6}} gutterBottom>
								Analyses:
							</Typography>
							<Typography variant="body2" sx={{ padding: '4px', paddingLeft: '20px' }}>
								{getAnalysisLabels(cocForm.cocSamples[0]).map((label: string, i: number) => (
									<span key={i} className="analysis-item">{label}</span>
								))}
							</Typography>
							</>
						)}

						<Typography variant="body2" sx={{ paddingLeft: '6px', paddingTop: "12px" }}>
							<strong>Please confirm that all samples are labelled with their Sample # listed above before shipping.</strong>
						</Typography>
						</>
					) : (
						<>
						<Typography variant="body1" sx={{ padding: "8px" }}>
							<table>
								<tr>
									<td style={{ paddingRight: 10 }}><strong>Submission date:</strong></td>
									<td>{cocForm.submissionDate ? dateFormatOptions.format(new Date(cocForm.submissionDate)) : ''}</td>
								</tr>
								<tr>
									<td style={{ paddingRight: 10, verticalAlign: 'top' }}><strong>Sent to:</strong></td>
									<td>LuminUltra Technologies Inc. Attn: Lab Services <br></br>
										{cocForm.labId === 1 ? '805 Pinnacle Dr, Suite M, Linthicum Heights, MD, 21090' : 'Unknown'}</td>
								</tr>
								<tr>
									<td style={{ height: 15 }}></td>
								</tr>
								<tr>
									<td style={{ paddingRight: 10 }}><strong>{isChampionX ? "ChampionX contact name:" : "Contact name:"}</strong></td>
									<td>{cocForm.contactName}</td>
								</tr>
								{cocForm.company && (
									<tr>
										<td style={{ paddingRight: 10 }}><strong>{isChampionX ? "ChampionX customer name:" : "Company:"}</strong></td>
										<td>{cocForm.company}</td>
									</tr>
								)}
								<tr>
									<td style={{ paddingRight: 10, verticalAlign: 'top' }}><strong>Address:</strong></td>
									<td>{createAddressString()}</td>
								</tr>
								{!isChampionX && (
									<tr>
										<td style={{ paddingRight: 10 }}><strong>Contact phone:</strong></td>
										<td>{cocForm.addressInfo.officePhone}</td>
									</tr>
								)}
								<tr>
									<td style={{ paddingRight: 10 }}><strong>Email:</strong> </td>
									<td>{cocForm.email}</td>
								</tr>
								<tr>
									<td style={{ paddingRight: 10, minWidth: 157, verticalAlign: 'top' }}><strong>Results shared with:</strong></td>
									<td>
										{cocForm.collaboratorEmails.length > 0 ? (
											<ul style={{ margin: 0, padding: 0, listStyleType: 'disc', listStylePosition: 'inside' }}>
												{cocForm.collaboratorEmails.map((email: string) => (
													<li key={email}>{email}</li>
												))}
											</ul>
										) : (
											<span>None</span>
										)}
									</td>
								</tr>
								{cocForm.purchaseOrderNumber && (
									<tr>
										<td style={{ paddingRight: 10 }}><strong>Purchase order number:</strong></td>
										<td>{cocForm.purchaseOrderNumber}</td>
									</tr>
								)}
								{isChampionX && cocForm.costCenterNumber && (
									<tr>
										<td style={{ paddingRight: 10 }}><strong>Cost center #:</strong></td>
										<td>{cocForm.costCenterNumber}</td>
									</tr>
								)}
								{cocForm.quoteNumber && (
									<tr>
										<td style={{ paddingRight: 10 }}><strong>Quote number:</strong></td>
										<td>{cocForm.quoteNumber}</td>
									</tr>
								)}
								{!isChampionX && (
									<tr>
										<td style={{ paddingRight: 10 }}><strong>Rush order:</strong></td>
										<td> {(cocForm.rushOrder === 'true' || cocForm.rushOrder === 'Y') ? 'Yes' : 'No'}</td>
									</tr>
								)}
							</table>
						</Typography>
						<Typography style={{fontSize:18, fontFamily:"inter", marginTop:15, fontWeight:600, marginBottom:6, paddingLeft:8}} gutterBottom>
							Samples:
							</Typography>
						
						<Typography variant="subtitle2" sx={{ padding: "6px" }}>
							<table style={{ width: '100%', borderCollapse: 'collapse' }}>
								<thead>
									<tr style={{ borderBottom: '1px solid #e0e0e0' }}>
										<th style={{ fontWeight: 600, padding: '6px' }}>{isReview === false ? "Inventory ID" : "Sample #"}</th>
										<th style={{ fontWeight: 600, padding: '6px' }}>{isChampionX ? "Sample Point" : "Sample Name"}</th>
										<th style={{ fontWeight: 600, padding: '6px' }}>Type</th>
										<th style={{ fontWeight: 600, padding: '6px' }}>Amount</th>
										<th style={{ fontWeight: 600, padding: '6px' }}>Collected</th>
										<th style={{ fontWeight: 600, padding: '6px' }}>{isChampionX ? "Customer Location Code (API14)" : "Site"}</th>
										{!isChampionX && (<th style={{ fontWeight: 600, padding: '6px' }}>Location</th>)}
									</tr>
								</thead>
								<tbody>
									{cocForm.cocSamples.map((sample: CocSample) => (
										<tr key={sample.inventoryNumber} style={{ borderBottom: '1px solid #e0e0e0' }}>
											<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
												{sample.inventoryId}
											</td>
											<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
												{sample.name}
											</td>
											<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
												{sample.type}
											</td>
											<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
												{sample.amount} {sample.units}
											</td>
											<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
												{smallerDateFormatOption.format(new Date(sample.collectionDate))}
											</td>
											<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
												{sample.siteName}
											</td>
											{!isChampionX && (
												<td style={{ margin: 'auto', padding: '6px', textAlign: 'center' }}>
													{sample.locationName}
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						</Typography>

						{isReview === true && 
							<Typography variant="body1" sx={{ padding: "8px" }}>
								<span>
									<strong>Please confirm that all samples are labelled with their Sample # listed above before shipping.</strong>
									<br></br>
								</span>
							</Typography>
						}

						<Typography variant="body1" sx={{ padding: "8px" }}>
							{!isChampionX && (
								 <span>
									<strong>Special handling requests:</strong> {cocForm.specialRequests ?? 'None'}
									<br></br>
								</span>
							)}
						</Typography>
						
						<Typography style={{fontSize:18, fontFamily:"inter", marginTop:15, fontWeight:600, marginBottom:6, paddingLeft:8}} gutterBottom>
							Analyses: 
							</Typography>
						<Typography variant="body1" sx={{ padding: "8px", paddingLeft: "26px" }}>
							{cocForm.cocAnalysisVOs.map((analysis: CocAnalysisVO) => (
								<li key={analysis.id}>{analysis.name} {analysis.variant !== 'N/A' ? '- ' + analysis.variant : ''}</li>
							))}
						</Typography>
						</>
					)}

				</DialogContent>

				{isReview === false &&
					<Box className="no-print" sx={{ textAlign: "center", paddingBottom: "16px" }}>
						<PrimaryButton onClick={printLabRequest}>Print</PrimaryButton>
					</Box>
				}

				{isReview === true &&
					<Box className="no-print" sx={{ textAlign: "right", paddingBottom: "16px", paddingRight: "16px" }}>
						<Button 
							onClick={ () => {
								console.log(cocForm)
								addEventLog(`CoC Form Edit Clicked`, "Lab Request", undefined, undefined);
								onClose();
							}}>
								Edit
						</Button>
						<PrimaryButton onClick={reviewFunction}>Submit</PrimaryButton>
					</Box>
				}
				
			</Dialog>
		</ThemeProvider>

	);
};

export default LabRequestDetails;